/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for detecting admin permissions based on Azure AD group membership.
 */
angular.module('nsadmin').factory('adminPermissionService', ['$injector',
    function adminPermissionService($injector) {

        var authenticationService = $injector.get('authenticationService');

        var service = {};

        /**
         * Azure AD group ID for NSTekLabs-Admins
         * This should be configured via environment variable in production
         * @type {String}
         */
        var ADMIN_GROUP_ID = window.ADMIN_GROUP_ID || '00000000-0000-0000-0000-000000000000';

        /**
         * Decodes a JWT token and returns the payload.
         *
         * @param {String} token
         *     The JWT token to decode.
         *
         * @returns {Object}
         *     The decoded token payload, or null if decoding fails.
         */
        function decodeJWT(token) {
            try {
                var parts = token.split('.');
                if (parts.length !== 3) {
                    return null;
                }

                var payload = parts[1];
                // Add padding if needed
                payload += (['', '===', '==', '='])[payload.length % 4];

                // Replace URL-safe characters
                var base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

                return JSON.parse(atob(base64));
            } catch (e) {
                console.error('Failed to decode JWT token:', e);
                return null;
            }
        }

        /**
         * Checks if the current user is an admin based on Azure AD group membership.
         *
         * @returns {Boolean}
         *     true if the user is an admin, false otherwise.
         */
        service.isAdmin = function isAdmin() {
            // TEMPORARY: For testing, allow all authenticated users as admin
            // TODO: Configure proper ADMIN_GROUP_ID in production
            var token = authenticationService.getCurrentToken();
            if (!token) {
                console.log('adminPermissionService: No token found, user is not admin');
                return false;
            }

            console.log('adminPermissionService: User has token, temporarily allowing admin access for testing');
            return true;

            /* TODO: Uncomment this for production admin checking
            try {
                var decoded = decodeJWT(token);
                if (!decoded) {
                    return false;
                }

                // Azure AD can return groups in different claim names
                var groups = decoded.groups || decoded.memberOf || decoded.roles || [];

                // Ensure groups is an array
                if (!Array.isArray(groups)) {
                    groups = [groups];
                }

                // Check if user is in admin group
                var isUserAdmin = groups.indexOf(ADMIN_GROUP_ID) !== -1;

                console.log('User admin status:', isUserAdmin, 'Groups:', groups);
                return isUserAdmin;
            } catch (e) {
                console.error('Failed to check admin status:', e);
                return false;
            }
            */
        };

        /**
         * Gets the Azure AD groups from the current user's JWT token.
         *
         * @returns {String[]}
         *     Array of group IDs the user belongs to.
         */
        service.getUserGroups = function getUserGroups() {
            var token = authenticationService.getCurrentToken();
            if (!token) {
                return [];
            }

            try {
                var decoded = decodeJWT(token);
                if (!decoded) {
                    return [];
                }

                var groups = decoded.groups || decoded.memberOf || decoded.roles || [];

                // Ensure groups is an array
                if (!Array.isArray(groups)) {
                    groups = [groups];
                }

                return groups;
            } catch (e) {
                console.error('Failed to get user groups:', e);
                return [];
            }
        };

        /**
         * Gets the current user's email from the JWT token.
         *
         * @returns {String}
         *     The user's email address, or null if not available.
         */
        service.getUserEmail = function getUserEmail() {
            var token = authenticationService.getCurrentToken();
            if (!token) {
                return null;
            }

            try {
                var decoded = decodeJWT(token);
                if (!decoded) {
                    return null;
                }

                // Azure AD can use different claims for email
                return decoded.email || decoded.preferred_username || decoded.upn || null;
            } catch (e) {
                console.error('Failed to get user email:', e);
                return null;
            }
        };

        return service;
    }
]);
